import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TOPIC_PROMPTS, TOPIC_TITLES, TOPIC_DESCRIPTIONS } from './TopicData';

const ALL_PROMPTS = Object.values(TOPIC_PROMPTS).flat();

function getRandomPrompt(pool, exclude) {
    let prompt = pool[Math.floor(Math.random() * pool.length)];
    while (prompt === exclude) {
        prompt = pool[Math.floor(Math.random() * pool.length)];
    }
    return prompt;
}

function FocusMode() {
    const { topic } = useParams();
    const pool = topic && TOPIC_PROMPTS[topic] ? TOPIC_PROMPTS[topic] : ALL_PROMPTS;

    const [currentPrompt, setCurrentPrompt] = useState(() => getRandomPrompt(pool));
    const [history, setHistory] = useState([]);
    const [time, setTime] = useState("");

    // Update prompt if topic changes
    useEffect(() => {
        const newPool = topic && TOPIC_PROMPTS[topic] ? TOPIC_PROMPTS[topic] : ALL_PROMPTS;
        setCurrentPrompt(getRandomPrompt(newPool));
    }, [topic]);

    // Clock
    useEffect(() => {
        const updateClock = () => {
            setTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        };
        updateClock();
        const interval = setInterval(updateClock, 1000);
        return () => clearInterval(interval);
    }, []);

    const handleGenerate = () => {
        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        setHistory(prev => {
            const newHistory = [{ text: currentPrompt, time: timestamp }, ...prev];
            return newHistory.slice(0, 3);
        });

        setCurrentPrompt(getRandomPrompt(pool, currentPrompt));
    };

    const pageTitle = topic && TOPIC_TITLES[topic]
        ? `${TOPIC_TITLES[topic]} | Mindful Journal Prompts`
        : 'Mindful Journal Prompts | Daily Prompts for Focus, Clarity & Self-Discovery';

    const pageDescription = topic && TOPIC_DESCRIPTIONS[topic]
        ? TOPIC_DESCRIPTIONS[topic]
        : 'Free mindful journal prompts for anxiety relief, self-discovery, shadow work, motivation, and mental clarity. Generate a personalized prompt and start journaling today.';

    const canonicalUrl = `https://www.mindfuljournalprompts.com${topic ? `/prompts/${topic}` : ''}`;

    const websiteSchema = !topic ? {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Mindful Journal Prompts",
        "url": "https://www.mindfuljournalprompts.com",
        "description": pageDescription
    } : null;

    return (
        <div className="fade-in">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:site_name" content="Mindful Journal Prompts" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                {websiteSchema && (
                    <script type="application/ld+json">
                        {JSON.stringify(websiteSchema)}
                    </script>
                )}
            </Helmet>

            <span className="meta">SESSION // <span id="clock">{time}</span></span>

            <h1 className="prompt-text">
                {currentPrompt}
            </h1>

            <div className="controls">
                <button className="gen-btn" onClick={handleGenerate}>Generate Prompt</button>
                <Link to="/personalized-prompt" className="text-btn" style={{ marginTop: '1rem', display: 'inline-block' }}>
                    Or get a personalized prompt...
                </Link>
            </div>

            {history.length > 0 && (
                <div className="history-log">
                    <span className="history-label">Previous Reflections</span>
                    <div id="history-list">
                        {history.map((item, index) => (
                            <div key={index} className="history-item">
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80%' }}>
                                    {item.text}
                                </span>
                                <span className="history-time">{item.time}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default FocusMode;
